// src/components/common/NotificationCenter.tsx
import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  Notification,
} from '../../store/slices/notificationsSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useUI } from '../../context/UIContext';

import {
  NotificationsIcon,
  CalendarIcon,
  ChatIcon,
  StarIcon,
  LoyaltyIcon,
  AlertIcon,
  CheckCircleIcon,
  CloseIcon,
} from './Icons';

interface NotificationCenterProps {
  onClose?: () => void;
  onViewAll?: () => void;
  /** `embedded`: list inside dashboard slide-over (no duplicate bell). Default `dropdown` keeps header bell + popover. */
  variant?: 'dropdown' | 'embedded';
  /** For deep links when pathname is ambiguous */
  userType?: 'client' | 'stylist' | 'admin';
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  onClose,
  onViewAll,
  variant = 'dropdown',
  userType: userTypeProp,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useUI();

  const [isOpen, setIsOpen] = React.useState(variant === 'embedded');
  const notificationRef = useRef<HTMLDivElement>(null);

  const { notifications, unreadCount, isLoading } = useAppSelector(
    (state) => state.notifications
  );

  const embedded = variant === 'embedded';

  useEffect(() => {
    dispatch(fetchNotifications({ limit: 30, page: 1 }));
  }, [dispatch]);

  useEffect(() => {
    if (embedded) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, embedded]);

  const resolveUserSegment = (): 'client' | 'stylist' | 'admin' => {
    if (userTypeProp) return userTypeProp;
    if (location.pathname.startsWith('/stylist')) return 'stylist';
    if (location.pathname.startsWith('/admin')) return 'admin';
    return 'client';
  };

  const handleOpenNotifications = () => {
    setIsOpen(true);
    dispatch(fetchNotifications({ limit: 30, page: 1 }));
  };

  const handleCloseNotifications = () => {
    setIsOpen(false);
    onClose?.();
  };

  const navigateForNotification = (n: Notification) => {
    const segment = resolveUserSegment();

    if (n.apiType.includes('appointment_')) {
      let appointmentId: string | undefined = n.linkedId;
      const data = n.data as Record<string, unknown> | undefined;
      if (!appointmentId && data) {
        const aid = data.appointmentId as unknown;
        if (typeof aid === 'string') appointmentId = aid;
        else if (aid && typeof aid === 'object' && '_id' in (aid as object)) {
          appointmentId = String((aid as { _id: string })._id);
        }
        const details = data.appointmentDetails as { _id?: string } | undefined;
        if (!appointmentId && details?._id) appointmentId = details._id;
      }
      if (appointmentId) {
        navigate(`/${segment}/appointment/${appointmentId}`);
        return;
      }
    }

    if (n.apiType.includes('salon_invitation_')) {
      navigate(`/${segment}/invitations`);
      return;
    }

    const data = n.data as Record<string, unknown> | undefined;
    const deep = data?.action as { type?: string; url?: string } | undefined;
    if (deep?.type === 'deep_link' && deep.url) {
      navigate(String(deep.url));
      return;
    }
    if (n.action?.type === 'deep_link' && n.action.url) {
      navigate(n.action.url);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await dispatch(markNotificationAsRead(notification.id)).unwrap();
      } catch {
        showSnackbar('Could not mark notification as read', 'error');
      }
    }
    navigateForNotification(notification);
    if (!embedded) handleCloseNotifications();
  };

  const handleMarkAllAsRead = async () => {
    try {
      await dispatch(markAllNotificationsAsRead()).unwrap();
      dispatch(fetchNotifications({ limit: 30, page: 1 }));
    } catch {
      showSnackbar('Failed to mark notifications as read', 'error');
    }
  };

  const handleDeleteNotification = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    dispatch(deleteNotification(id));
  };

  const getNotificationIcon = (n: Notification) => {
    const t = (n.apiType || n.type || '').toLowerCase();
    if (t.includes('appointment')) {
      return <CalendarIcon size={20} color="#C9A84C" />;
    }
    if (t.includes('message') || t.includes('chat')) {
      return <ChatIcon size={20} color="#C9A84C" />;
    }
    if (t.includes('review')) {
      return <StarIcon size={20} color="#ffab00" />;
    }
    if (t.includes('loyalty') || t.includes('tier') || t.includes('points')) {
      return <LoyaltyIcon size={20} color="#36b37e" />;
    }
    return <AlertIcon size={20} color="#7a869a" />;
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return timestamp;
    }
  };

  const panelBody = (
    <>
      <div
        className={`notifications__header${
          embedded ? ' notifications__header--embedded' : ''
        }`}
      >
        {!embedded && (
          <h3 className="notifications__title">Notifications</h3>
        )}
        {unreadCount > 0 && (
          <button
            type="button"
            className="notifications__mark-all"
            onClick={handleMarkAllAsRead}
          >
            <CheckCircleIcon size={16} />
            <span>Mark all as read</span>
          </button>
        )}
      </div>

      <div className="notifications__divider" />

      <div className="notifications__content">
        {isLoading ? (
          <div className="notifications__loading">
            <div className="loading-spinner" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications__empty">
            <p>No notifications</p>
          </div>
        ) : (
          <ul className="notifications__list">
            {notifications.map((notification) => (
              <li
                key={notification.id}
                className={`notification-item ${
                  !notification.read ? 'notification-item--unread' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-item__icon">
                  {getNotificationIcon(notification)}
                </div>

                <div className="notification-item__content">
                  <h4 className="notification-item__title">{notification.title}</h4>
                  <p className="notification-item__message">{notification.message}</p>
                  <div className="notification-item__time">
                    {formatTimestamp(notification.createdAt)}
                  </div>
                </div>

                <button
                  type="button"
                  className="notification-item__delete"
                  onClick={(e) => handleDeleteNotification(e, notification.id)}
                  aria-label="Delete notification"
                >
                  <CloseIcon size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="notifications__footer">
        <button
          type="button"
          className="notifications__view-all"
          onClick={() => {
            if (onViewAll) {
              onViewAll();
            } else {
              navigate(`/${resolveUserSegment()}/notifications`);
            }
            handleCloseNotifications();
          }}
        >
          View All Notifications
        </button>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="notifications notifications--embedded" ref={notificationRef}>
        {panelBody}
      </div>
    );
  }

  return (
    <div className="notifications" ref={notificationRef}>
      <button
        type="button"
        className="notifications__button"
        onClick={handleOpenNotifications}
        aria-label="Notifications"
        title="Notifications"
      >
        <div className="notifications__icon">
          <NotificationsIcon size={24} />
          {unreadCount > 0 && (
            <span className="notifications__badge">{unreadCount}</span>
          )}
        </div>
      </button>

      {isOpen && (
        <div className="notifications__panel">
          {panelBody}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
